// src/pages/ReportsPage.js
import React from 'react';
import toast from 'react-hot-toast';
import { reports, downloadBlob } from '../utils/api';

const REPORT_CARDS = [
  {
    title: 'Student Report',
    desc: 'Full list of all registered students with course, school, year, and status.',
    icon: '👥',
    accent: '#00d4ff',
    onJson: () => reports.students('json'),
    onCsv: () => reports.students('csv'),
    csvName: 'students.csv'
  },
  {
    title: 'Device Report',
    desc: 'All registered devices with MAC address, vendor, type, status, and student info.',
    icon: '📱',
    accent: '#7c83fd',
    onJson: () => reports.devices('json'),
    onCsv: () => reports.devices('csv'),
    csvName: 'devices.csv'
  },
  {
    title: 'Audit Log',
    desc: 'Complete audit trail of all admin actions — creates, edits, status changes.',
    icon: '📋',
    accent: '#ffab40',
    onJson: () => reports.auditLogs('json'),
    onCsv: () => reports.auditLogs('csv'),
    csvName: 'audit_logs.csv'
  },
  {
    title: 'RADIUS Accounting',
    desc: 'Network session accounting log from FreeRADIUS — connection times, data usage.',
    icon: '📡',
    accent: '#00e676',
    onJson: () => reports.accounting('json'),
    onCsv: () => reports.accounting('csv'),
    csvName: 'radius_accounting.csv'
  }
];

export default function ReportsPage() {
  const handleDownload = async (fetchFn, filename, format) => {
    try {
      const res = await fetchFn();
      if (format === 'csv') {
        downloadBlob(res.data, filename);
        toast.success(`${filename} downloaded`);
      } else {
        const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
        downloadBlob(blob, filename.replace('.csv', '.json'));
        toast.success('JSON downloaded');
      }
    } catch (err) {
      toast.error('Export failed');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">📊 Reports & Exports</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 4 }}>
            Download data exports in CSV or JSON format
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: 20 }}>
        {REPORT_CARDS.map(card => (
          <div key={card.title} className="card" style={{ borderTop: `3px solid ${card.accent}` }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 16 }}>
              <div style={{
                width: 48, height: 48, background: `${card.accent}18`, borderRadius: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.4rem', flexShrink: 0
              }}>{card.icon}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 4 }}>{card.title}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', lineHeight: 1.5 }}>{card.desc}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => handleDownload(card.onCsv, card.csvName, 'csv')}
              >
                ⬇ Download CSV
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handleDownload(card.onJson, card.csvName, 'json')}
              >
                { } Download JSON
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header"><span className="card-title">Backup Recommendations</span></div>
        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', lineHeight: 1.8 }}>
          <p>🔄 <strong>Daily automated backups</strong> — Schedule with <code style={{ background: 'var(--bg-hover)', padding: '2px 6px', borderRadius: 4 }}>mysqldump radius_manager | gzip &gt; backup_$(date +%Y%m%d).sql.gz</code></p>
          <p>📁 <strong>Store offsite</strong> — Copy dumps to a remote server or cloud storage (S3, Google Cloud Storage).</p>
          <p>🔐 <strong>Encrypt backups</strong> — Use GPG encryption: <code style={{ background: 'var(--bg-hover)', padding: '2px 6px', borderRadius: 4 }}>gpg --encrypt --recipient admin@example.com backup.sql.gz</code></p>
          <p>✅ <strong>Test restores quarterly</strong> — Verify backup integrity by restoring to a test MySQL instance.</p>
        </div>
      </div>
    </div>
  );
}
